/**
 * Error shape returned by CABES on failure, e.g.
 * {
 *   "code": 400,
 *   "type": "/request/invalid",
 *   "message": "BAD_REQUEST",
 *   "fields": [],
 *   "exceptionMessage": "Parameters must not be blank.",
 *   "stackTrace": null
 * }
 */
export type CabesErrorBody = {
    code?: number;
    type?: string;
    message?: string;
    fields?: unknown[];
    exceptionMessage?: string | null;
    stackTrace?: string | null;
};

/**
 * Error thrown for a failed API call. `message` is always safe to show to the
 * user: it is either a translated fallback or a message CABES sent back in
 * `exceptionMessage`. `serverMessage` is set only in the latter case so callers
 * can tell a pass-through message apart from a translated one.
 */
export class ApiError extends Error {
    readonly status?: number;
    readonly serverMessage?: string;

    constructor(message: string, options: { status?: number; serverMessage?: string } = {}) {
        super(message);
        this.name = 'ApiError';
        this.status = options.status;
        this.serverMessage = options.serverMessage;

        // Required so `instanceof ApiError` works after TS downlevels to ES5.
        Object.setPrototypeOf(this, ApiError.prototype);
    }
}

/**
 * Pull a displayable message out of a CABES error body. Returns undefined when
 * the body isn't a CABES error, when `exceptionMessage` is absent/blank, or
 * when it doesn't look like something we should show a user (a stack trace,
 * a class name, or an over-long blob).
 */
export function extractServerMessage(responseBody: unknown): string | undefined {
    if (!responseBody || typeof responseBody !== 'object' || Array.isArray(responseBody)) return undefined;

    const { exceptionMessage } = responseBody as CabesErrorBody;
    if (typeof exceptionMessage !== 'string') return undefined;

    const trimmed = exceptionMessage.trim();
    if (!trimmed) return undefined;
    if (trimmed.length > MAX_SERVER_MESSAGE_LENGTH) return undefined;
    if (LOOKS_INTERNAL.test(trimmed)) return undefined;

    return trimmed;
}

const MAX_SERVER_MESSAGE_LENGTH = 200;

// Java exception dumps / class names leaking through, e.g.
// "java.lang.IllegalArgumentException: ..." or "\tat com.foo.Bar(Bar.java:12)".
const LOOKS_INTERNAL = /(^\s*at\s)|(\b[a-z][a-z0-9]*(\.[a-z0-9]+)+\.[A-Z][A-Za-z0-9]*\b)|[\n\r]/;
